import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  where,
  type DocumentReference,
} from 'firebase/firestore'
import { deleteToken, getToken, onMessage, type MessagePayload } from 'firebase/messaging'
import { firestore, getFirebaseMessaging } from '@core/services/firebase/firebase-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'

const USERS_COLLECTION = 'users'
const FCM_TOKENS_SUBCOLLECTION = 'fcm_tokens'
const USER_PUSH_NOTIFICATIONS_COLLECTION = 'ff_user_push_notifications'
export const WEB_DEVICE_TYPE = 'web'
const MESSAGING_SERVICE_WORKER_PATH = '/firebase-messaging-sw.js'

export interface TriggerPushNotificationInput {
  notificationTitle: string
  notificationText: string
  userRefs: string[]
  initialPageName: string
  targetDeviceType?: string
  targetDeviceTypes?: string[]
  parameterData?: Record<string, unknown>
  sender: string
  timestamp?: Date
  notificationImageUrl?: string
  scheduledTime?: Date
  notificationSound?: string
}

function getFirestoreOrThrow() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return firestore
}

function getVapidPublicKeyOrThrow(): string {
  const vapidPublicKey = import.meta.env.VITE_FIREBASE_VAPID_KEY?.trim()
  if (!vapidPublicKey) {
    throw new CoreError({
      cause: CoreErrorCause.PushNotificationsRequestFailure,
      context: 'Missing VITE_FIREBASE_VAPID_KEY for web push notifications.',
    })
  }
  return vapidPublicKey
}

async function ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    throw new CoreError({
      cause: CoreErrorCause.PushNotificationsRequestFailure,
      context: 'Service workers are not supported in this browser.',
    })
  }

  const scriptUrl = new URL(MESSAGING_SERVICE_WORKER_PATH, window.location.origin).toString()

  return navigator.serviceWorker.register(scriptUrl, {
    scope: '/',
    updateViaCache: 'none',
  })
}

function getUserDocRef(userId: string): DocumentReference {
  const appFirestore = getFirestoreOrThrow()
  return doc(appFirestore, USERS_COLLECTION, userId)
}

function getUserFcmTokensCollection(userId: string) {
  const appFirestore = getFirestoreOrThrow()
  return collection(appFirestore, USERS_COLLECTION, userId, FCM_TOKENS_SUBCOLLECTION)
}

function normalizeUserId(userId: string): string {
  return userId.trim()
}

function normalizeFcmToken(fcmToken: string): string {
  return fcmToken.trim()
}

function normalizeTargetDeviceType(deviceType?: string): string | undefined {
  const normalizedValue = deviceType?.trim()
  if (!normalizedValue) {
    return undefined
  }
  return normalizedValue
}

function normalizeTargetDeviceTypes(deviceTypes?: string[]): string[] | undefined {
  if (!deviceTypes) {
    return undefined
  }

  const normalizedValues = Array.from(
    new Set(
      deviceTypes
        .map((value) => value.trim())
        .filter((value) => value.length > 0),
    ),
  )

  if (normalizedValues.length === 0) {
    return undefined
  }

  return normalizedValues
}

function toTokenDocumentId(fcmToken: string): string {
  return normalizeFcmToken(fcmToken).replaceAll('/', '_')
}

export class PushNotificationsService {
  private static instance: PushNotificationsService

  private constructor() {}

  public static getInstance(): PushNotificationsService {
    if (!PushNotificationsService.instance) {
      PushNotificationsService.instance = new PushNotificationsService()
    }
    return PushNotificationsService.instance
  }

  public async isPushSupported(): Promise<boolean> {
    return (await getFirebaseMessaging()) !== null
  }

  public async requestNotificationPermission(): Promise<NotificationPermission> {
    const supported = await this.isPushSupported()
    if (!supported) {
      return 'denied'
    }
    return Notification.requestPermission()
  }

  public async getFcmToken(): Promise<string | null> {
    const messaging = await getFirebaseMessaging()
    if (!messaging) {
      return null
    }

    if (Notification.permission !== 'granted') {
      return null
    }

    const vapidKey = getVapidPublicKeyOrThrow()
    const serviceWorkerRegistration = await ensureServiceWorkerRegistration()
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration,
    })

    return token ? normalizeFcmToken(token) : null
  }

  public async addPushNotificationsToken(userId: string): Promise<string | null> {
    const normalizedUserId = normalizeUserId(userId)
    if (!normalizedUserId) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'Cannot store push notification token for an empty user id.',
      })
    }

    const permission = await this.requestNotificationPermission()
    if (permission !== 'granted') {
      return null
    }

    const fcmToken = await this.getFcmToken()
    if (!fcmToken) {
      throw new CoreError({
        cause: CoreErrorCause.PushNotificationsRequestFailure,
        context: 'FCM token is not available after granting notification permission.',
      })
    }

    const userTokensCollection = getUserFcmTokensCollection(normalizedUserId)
    const tokenDocId = toTokenDocumentId(fcmToken)
    const tokenDocRef = doc(userTokensCollection, tokenDocId)
    const existingTokenDoc = await getDoc(tokenDocRef)
    if (existingTokenDoc.exists()) {
      return existingTokenDoc.id
    }

    await setDoc(tokenDocRef, {
      fcm_token: fcmToken,
      device_type: WEB_DEVICE_TYPE,
      created_at: new Date(),
    })

    return tokenDocId
  }

  public async removePushNotificationsToken(userId: string): Promise<void> {
    const normalizedUserId = normalizeUserId(userId)
    if (!normalizedUserId) {
      return
    }

    const messaging = await getFirebaseMessaging()
    if (!messaging) {
      return
    }

    if (Notification.permission !== 'granted') {
      return
    }

    const currentToken = await this.getFcmToken()
    if (!currentToken) {
      return
    }

    const userTokensCollection = getUserFcmTokensCollection(normalizedUserId)
    const tokenDocId = toTokenDocumentId(currentToken)
    await deleteDoc(doc(userTokensCollection, tokenDocId))

    const existingTokensQuery = await getDocs(
      query(
        userTokensCollection,
        where('fcm_token', '==', currentToken),
        limit(20),
      ),
    )

    await Promise.all(existingTokensQuery.docs.map((item) => deleteDoc(item.ref)))

    await deleteToken(messaging)
  }

  public async triggerPushNotification(input: TriggerPushNotificationInput): Promise<string> {
    const normalizedSender = normalizeUserId(input.sender)
    if (!normalizedSender) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'Cannot trigger push notification without a sender user id.',
      })
    }

    const normalizedUserRefs = input.userRefs
      .map((value) => value.trim())
      .filter((value) => value.length > 0)

    if (normalizedUserRefs.length === 0) {
      throw new CoreError({
        cause: CoreErrorCause.DataNotFound,
        context: 'Cannot trigger push notification without target user refs.',
      })
    }

    const normalizedTargetDeviceType = normalizeTargetDeviceType(input.targetDeviceType)
    const normalizedTargetDeviceTypes = normalizeTargetDeviceTypes(input.targetDeviceTypes)

    const appFirestore = getFirestoreOrThrow()
    const createdDoc = await addDoc(collection(appFirestore, USER_PUSH_NOTIFICATIONS_COLLECTION), {
      notification_title: input.notificationTitle.trim(),
      notification_text: input.notificationText.trim(),
      notification_image_url: input.notificationImageUrl?.trim() || null,
      scheduled_time: input.scheduledTime ?? null,
      notification_sound: input.notificationSound?.trim() || null,
      user_refs: normalizedUserRefs.join(','),
      initial_page_name: input.initialPageName.trim() || '/',
      parameter_data: JSON.stringify(input.parameterData ?? {}),
      sender: getUserDocRef(normalizedSender),
      timestamp: input.timestamp ?? new Date(),
      ...(normalizedTargetDeviceType
        ? { target_device_type: normalizedTargetDeviceType }
        : {}),
      ...(normalizedTargetDeviceTypes
        ? { target_device_types: normalizedTargetDeviceTypes }
        : {}),
    })

    return createdDoc.id
  }
}

const pushNotificationsService = PushNotificationsService.getInstance()

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  return pushNotificationsService.requestNotificationPermission()
}

export async function addPushNotificationsToken(userId: string): Promise<string | null> {
  return pushNotificationsService.addPushNotificationsToken(userId)
}

export async function removePushNotificationsToken(userId: string): Promise<void> {
  return pushNotificationsService.removePushNotificationsToken(userId)
}

export async function triggerPushNotification(input: TriggerPushNotificationInput): Promise<string> {
  return pushNotificationsService.triggerPushNotification(input)
}

export function observeForegroundPushMessages(
  listener: (payload: MessagePayload) => void,
): () => void {
  let active = true
  let unsubscribe = () => {}

  void getFirebaseMessaging()
    .then((messaging) => {
      if (!active || !messaging) {
        return
      }

      unsubscribe = onMessage(messaging, (payload) => {
        listener(payload)
      })
    })
    .catch(() => {})

  return () => {
    active = false
    unsubscribe()
  }
}
